import { css } from 'astroturf';
import React, { useId } from 'react';
import { Download } from 'react-bytesize-icons';

import InfoDialog from './_infoDialog';
import { createDialogId } from './_catalogCardUtils';
import { updateDialogSearchParams } from './_dialogSearchParams';
import SnippetCard from './_snippetCard';

const styles = css`
  .infoButton {
    border: 0;
    background: #0a153011;
    color: #39728d;
    width: 1.4rem;
    height: 1.4rem;
    border-radius: 100px;
    padding: 0;
    line-height: 1;
    font-size: 0.8rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition:
      0.15s ease-in-out background,
      0.15s ease-in-out color;

    &:hover,
    &:focus-visible {
      background: #0a1530;
      color: #fff;
    }
  }

  .cardHeader {
    position: relative;
    margin-top: 2rem;
    padding: 0rem 2rem 1.125rem;

    h4 {
      font-size: 1.125rem;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 0.5rem;
      margin: 0;

      small {
        font-size: 0.75em;
        color: #39728d;
        font-weight: 400;
      }
    }
  }

  .infobar {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.5rem;
  }

  .categories {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    padding-right: 0;
  }

  .category {
    font-size: 10px;
    border-radius: 100px;
    padding: 0.1rem 0.55rem;
    background: #00aeff21;
    color: #333;
  }

  .author {
    margin: 0;
    line-height: 1.25;
    font-size: 0.875rem;
    flex: 1 0 auto;

    b {
      font-weight: 500;
      color: #39728d;
    }
  }

  .collectionContent {
    padding: 1.5rem 2rem;
    margin-top: 0.5rem;
    background: rgba(0, 174, 255, 0.13);
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .dialogSnippetGrid {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .dialogActions {
    padding: 0 2rem 1rem;
  }

  .download {
    border: 0;
    padding: 0.6rem 0.75rem;
    border-radius: 4px;
    font-weight: 600;
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    background: rgb(16, 125, 181);
    color: #fff;
    transition: background 0.15s ease-in-out;

    &:hover,
    &:focus-visible {
      background: #008300;
    }

    svg {
      width: 14px;
      height: 14px;
      stroke-width: 3px;
    }
  }
`;

export default function CollectionInfoDialog({
  collection,
  normalizeType,
  onDownload,
  activeCollection = null,
  activeSnippet = null,
  renderTrigger = null,
}) {
  const instanceId = useId();
  const collectionParamValue = String(collection.name);

  const isOpen = activeCollection === collectionParamValue;

  const handleOpenChange = (nextIsOpen) => {
    updateDialogSearchParams((params) => {
      if (nextIsOpen) {
        params.set('collection', collectionParamValue);
        return;
      }

      if (params.get('collection') === collectionParamValue) {
        params.delete('collection');
      }
    }, nextIsOpen);
  };

  return (
    <InfoDialog
      id={createDialogId('collection-dialog', `${collection.name}-${instanceId}`)}
      triggerClassName={styles.infoButton}
      triggerLabel={`Show details for ${collection.name}`}
      triggerContent="i"
      renderTrigger={renderTrigger}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
    >
      <div className={styles.cardHeader}>
        <h4>
          {collection.name} <small>({collection.snippets.length})</small>
        </h4>
        {collection.description && <p>{collection.description}</p>}

        <div className={styles.infobar}>
          {collection.author && (
            <p className={styles.author}>
              By{' '}
              {collection.authorUrl ? (
                <a href={collection.authorUrl} target="_blank" rel="noopener noreferrer">
                  <b>{collection.author}</b>
                </a>
              ) : (
                <b>{collection.author}</b>
              )}
            </p>
          )}
          {!!collection.categories.length && (
            <div className={styles.categories}>
              {collection.categories.map((category) => (
                <span key={category} className={styles.category}>
                  {category}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={styles.dialogActions} data-card="collection">
        <button
          className={styles.download}
          type="button"
          onClick={() => onDownload(collection)}
          data-action="download-snippet"
        >
          <span>Download</span>
          <Download />
        </button>
        <pre hidden>{JSON.stringify(collection)}</pre>
      </div>
      <section className={styles.collectionContent}>
        <div className={styles.dialogSnippetGrid}>
          {collection.snippets.map((snippet) => (
            <SnippetCard
              key={snippet.id}
              snippet={snippet}
              isInCollection={false}
              onAdd={() => {}}
              onDownload={() => {}}
              normalizeType={normalizeType}
              hideActions
              collectionName={collection.name}
              activeSnippet={activeSnippet}
              activeCollection={activeCollection}
            />
          ))}
        </div>
      </section>
    </InfoDialog>
  );
}
